function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { getOrThrow } from '@deephaven/utils';
import GridMouseHandler from "../GridMouseHandler.js"; // The different properties that can be used by implementing classes, whether for rows or columns
/**
 * Abstract class that should be extended for column/row behaviour
 * Override the necessary functions/properties
 */
class GridSeparatorMouseHandler extends GridMouseHandler {
  constructor() {
    super(...arguments);
    // The index where dragging the column separator started
    _defineProperty(this, "draggingIndex", void 0);
    // The columns in the order they're being resized
    _defineProperty(this, "resizingItems", []);
    // Columns that were hidden under the separator when starting a drag
    _defineProperty(this, "hiddenItems", []);
    // The target width of the columns being resized
    _defineProperty(this, "targetSizes", new Map());
    _defineProperty(this, "dragOffset", 0);
  } // Override/Implement these properties/functions
  // End of overrides

  onDown(gridPoint, grid) {
    var metrics = grid.metrics;
    var model = grid.props.model;
    var theme = grid.getTheme();
    if (!metrics) throw new Error('metrics not set');
    var separator = this.getSeparator(gridPoint, metrics, model, theme);
    if (separator != null) {
      var separatorIndex = separator.index;
      this.dragOffset = 0;
      this.draggingIndex = separatorIndex;
      this.resizingItems = [separatorIndex];
      this.hiddenItems = this.getHiddenItems(separatorIndex, metrics).reverse();
      this.targetSizes.clear();
      this.addTargetSize(metrics, separatorIndex);
      this.updateCursor(metrics, separatorIndex);
      this.updateSeparator(grid, separator);
      return true;
    }
    return false;
  }
  onMove(gridPoint, grid) {
    var metrics = grid.metrics;
    var model = grid.props.model;
    var theme = grid.getTheme();
    if (!metrics) throw new Error('metrics not set');
    var separator = this.getSeparator(gridPoint, metrics, model, theme);
    if (separator != null) {
      this.updateCursor(metrics, separator.index);
      return true;
    }
    return false;
  }
  onDrag(gridPoint, grid) {
    if (this.draggingIndex == null) {
      return false;
    }
    var point = gridPoint[this.pointProperty];
    var metricCalculator = grid.metricCalculator,
      metrics = grid.metrics;
    if (!metrics) throw new Error('metrics not set');
    var theme = grid.getTheme();
    var visibleOffsets = metrics[this.visibleOffsetProperty];
    var margin = metrics[this.marginProperty];
    var calculatedSizes = metrics[this.calculatedSizesProperty];
    var modelIndexes = metrics[this.modelIndexesProperty];
    var firstIndex = metrics[this.firstIndexProperty];
    var treePadding = metrics[this.treePaddingProperty];

    // New sizes are batched and applied after the loop to avoid updating state while calculating next step
    var newSizes = new Map();

    // Use a loop as we may need to resize multiple items if they drag quickly
    var resizeIndex = this.resizingItems[this.resizingItems.length - 1];
    while (resizeIndex != null) {
      var itemOffset = getOrThrow(visibleOffsets, resizeIndex);
      var itemSize = point - margin - itemOffset - this.dragOffset;
      var _modelIndex = getOrThrow(modelIndexes, resizeIndex);
      var targetSize = this.targetSizes.get(_modelIndex);
      var isResizingMultiple = this.resizingItems.length > 1;
      var hiddenIndex = this.hiddenItems.indexOf(resizeIndex);
      var calculatedSize = calculatedSizes.get(_modelIndex);
      if (calculatedSize != null && resizeIndex === firstIndex) {
        calculatedSize += treePadding;
      }
      var newSize = itemSize;
      if (calculatedSize != null && Math.abs(itemSize - calculatedSize) <= theme.headerResizeSnapThreshold) {
        // Snapping behaviour to "natural" width
        newSize = calculatedSize;
      } else if (targetSize !== undefined && itemSize > targetSize && (isResizingMultiple && hiddenIndex !== 0 || hiddenIndex > 0)) {
        newSize = targetSize;
      } else if (itemSize <= theme.headerResizeHiddenSnapThreshold) {
        // Snapping to hidden item
        newSize = 0;
      }
      newSizes.set(_modelIndex, newSize);
      if (itemSize < -theme.headerResizeSnapThreshold && newSize === 0) {
        if (hiddenIndex >= 0 && isResizingMultiple) {
          var _this$targetSizes$get;
          this.resizingItems.pop();
          this.removeTargetSize(metrics, resizeIndex);
          resizeIndex = this.resizingItems[this.resizingItems.length - 1];
          this.dragOffset -= (_this$targetSizes$get = this.targetSizes.get(getOrThrow(modelIndexes, resizeIndex))) !== null && _this$targetSizes$get !== void 0 ? _this$targetSizes$get : 0;
        } else {
          resizeIndex = this.getNextShownItem(resizeIndex, metrics);
          if (resizeIndex !== null) {
            this.resizingItems.push(resizeIndex);
            this.addTargetSize(metrics, resizeIndex);
          }
        }
      } else if (targetSize !== undefined && itemSize > targetSize + theme.headerResizeSnapThreshold && newSize === targetSize) {
        if (hiddenIndex > 0) {
          this.dragOffset += newSize;
          resizeIndex = this.hiddenItems[hiddenIndex - 1];
          this.resizingItems.push(resizeIndex);
          this.addTargetSize(metrics, resizeIndex);
        } else if (isResizingMultiple) {
          this.resizingItems.pop();
          this.removeTargetSize(metrics, resizeIndex);
          resizeIndex = this.resizingItems[this.resizingItems.length - 1];
        } else {
          resizeIndex = null;
        }
      } else {
        resizeIndex = null;
      }
    }
    newSizes.forEach((newSize, modelIndex) => {
      var _metricCalculator$thi;
      var defaultSize = (_metricCalculator$thi = metricCalculator[this.initialSizesProperty].get(modelIndex)) !== null && _metricCalculator$thi !== void 0 ? _metricCalculator$thi : calculatedSizes.get(modelIndex);
      if (newSize === defaultSize) {
        this.resetSize(metricCalculator, modelIndex);
      } else {
        this.setSize(metricCalculator, modelIndex, newSize);
      }
    });
    this.updateCursor(metrics, this.draggingIndex);
    return true;
  }
  onUp(gridPoint, grid) {
    if (this.draggingIndex != null) {
      this.draggingIndex = undefined;
      this.resizingItems = [];
      this.hiddenItems = [];
      this.targetSizes.clear();
      this.updateSeparator(grid, null);
    }
    return false;
  }
  onDoubleClick(gridPoint, grid) {
    var metrics = grid.metrics,
      metricCalculator = grid.metricCalculator;
    var model = grid.props.model;
    var theme = grid.getTheme();
    if (!metrics) throw new Error('metrics not set');
    var separator = this.getSeparator(gridPoint, metrics, model, theme);
    if (separator != null) {
      var modelIndexes = metrics[this.modelIndexesProperty];
      var _modelIndex2 = getOrThrow(modelIndexes, separator.index);
      this.resetSize(metricCalculator, _modelIndex2);
      grid.forceUpdate();
      return true;
    }
    return false;
  }
  updateCursor(metrics, itemIndex) {
    var visibleSizes = metrics[this.visibleSizesProperty];
    var itemSize = getOrThrow(visibleSizes, itemIndex);
    if (itemSize === 0) {
      this.cursor = this.hiddenCursor;
    } else {
      this.cursor = this.defaultCursor;
    }
  }
  addTargetSize(metrics, itemIndex) {
    var modelIndexes = metrics[this.modelIndexesProperty];
    var userSizes = metrics[this.userSizesProperty];
    var calculatedSizes = metrics[this.calculatedSizesProperty];
    var treePadding = itemIndex === 0 ? metrics[this.treePaddingProperty] : 0;
    var modelIndex = getOrThrow(modelIndexes, itemIndex);
    var targetSize = userSizes.get(modelIndex);
    if (targetSize == null || targetSize === 0) {
      var _calculatedSizes$get;
      targetSize = ((_calculatedSizes$get = calculatedSizes.get(modelIndex)) !== null && _calculatedSizes$get !== void 0 ? _calculatedSizes$get : 0) + treePadding;
    }
    this.targetSizes.set(modelIndex, targetSize);
  }
  removeTargetSize(metrics, itemIndex) {
    var modelIndexes = metrics[this.modelIndexesProperty];
    var modelIndex = getOrThrow(modelIndexes, itemIndex);
    this.targetSizes.delete(modelIndex);
  }
}
export default GridSeparatorMouseHandler;
//# sourceMappingURL=GridSeparatorMouseHandler.js.map